import { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { ArrowLeft, Copy, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { testersApi, formatApiError } from "../lib/api";

const Row = ({ t, onToggle, onRemove }) => (
  <tr data-testid={`tester-row-${t.tester_id}`} className="border-t border-white/[0.06]">
    <td className="py-3 pr-4">
      <input type="checkbox" data-testid={`tester-added-${t.tester_id}`} checked={t.added} onChange={() => onToggle(t)} className="accent-[#D4FF3A]" />
    </td>
    <td className="py-3 pr-4 text-white">{t.name}</td>
    <td data-testid={`tester-gmail-${t.tester_id}`} className="py-3 pr-4 font-mono text-xs">{t.gmail}</td>
    <td className="py-3 pr-4 text-slate-400">{t.instagram ? `@${t.instagram}` : "—"}</td>
    <td className="py-3 pr-4 text-slate-500 font-mono text-xs">{t.created_at.slice(0, 10)}</td>
    <td className="py-3 text-right">
      <button data-testid={`tester-remove-${t.tester_id}`} onClick={() => onRemove(t)} className="text-slate-500 hover:text-red-300"><Trash2 size={14} /></button>
    </td>
  </tr>
);

export default function AdminTestersPage() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const load = () => testersApi.list().then(setData).catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
  useEffect(() => { if (user?.role === "admin") load(); }, [user]);
  if (user.role !== "admin") return <Navigate to="/" replace />;

  const run = async (fn) => {
    try { await fn(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    load();
  };
  const toggle = (t) => run(() => testersApi.mark(t.tester_id, !t.added));
  const remove = (t) => window.confirm(`Remove ${t.gmail}?`) && run(() => testersApi.remove(t.tester_id));
  const copy = (onlyNew) => {
    const list = data.testers.filter((t) => !onlyNew || !t.added).map((t) => t.gmail).join(", ");
    if (!list) { toast(onlyNew ? "No new emails to copy." : "No emails to copy yet."); return; }
    navigator.clipboard.writeText(list);
    toast.success("Emails copied. Paste them into Play Console → Testing → Closed testing → Testers.");
  };
  return (
    <div data-testid="admin-testers-page" className="min-h-screen bg-[#0B0F17] text-slate-300">
      <div className="max-w-5xl mx-auto px-6 sm:px-10 py-14">
        <Link to="/" data-testid="admin-testers-back" className="text-xs font-mono text-slate-500 hover:text-white flex items-center gap-1.5"><ArrowLeft size={13} /> WHAT?</Link>
        <h1 className="font-display text-4xl font-extrabold text-white mt-8">Android testers</h1>
        <p className="text-sm text-slate-400 mt-2">Share <span className="font-mono text-[#D4FF3A]">askwhat.co.za/testers</span> with your followers. Google Play closed testing needs about 12 testers for 14 days.</p>
        {data && (
          <>
            <div className="flex flex-wrap items-center gap-3 mt-8">
              <span data-testid="admin-testers-count" className="what-pill">{data.total} signed up · {data.added} added</span>
              <button data-testid="admin-testers-copy-new" onClick={() => copy(true)} className="what-btn-ghost flex items-center gap-1.5"><Copy size={13} /> Copy new emails</button>
              <button data-testid="admin-testers-copy-all" onClick={() => copy(false)} className="what-btn-ghost flex items-center gap-1.5"><Copy size={13} /> Copy all</button>
            </div>
            <table data-testid="admin-testers-table" className="w-full text-sm mt-6">
              <thead><tr className="text-left text-[11px] font-mono uppercase tracking-[0.14em] text-slate-500 [&>th]:pb-2 [&>th]:font-normal"><th>Added</th><th>Name</th><th>Gmail</th><th>Instagram</th><th>Joined</th><th /></tr></thead>
              <tbody>{data.testers.map((t) => <Row key={t.tester_id} t={t} onToggle={toggle} onRemove={remove} />)}</tbody>
            </table>
            {!data.total && <p data-testid="admin-testers-empty" className="text-slate-500 mt-6">No one has signed up yet.</p>}
          </>
        )}
      </div>
    </div>
  );
}
